import { For, Show, type JSX } from 'solid-js';
import { MUSIC_RATES, musicRateLabel, musicRateShortLabel, type MusicRate } from '../lib/musicRate';
import './MusicRateControl.css';
import { TEST_IDS, testIdOf } from '../testIds';

export interface MusicRateControlProps {
  /** The rate the CONTROLLER reports. The control is mirror-driven; it never holds its own truth. */
  value: string | undefined;
  onChange: (rate: MusicRate) => void;
  disabled?: boolean;
  rates?: readonly MusicRate[];
  /** Test id prefix, so a card can address its own control (e.g. "sweep" → "sweep-music-rate"). */
  idPrefix?: string;
}

/**
 * The shared lock interval selector. An unrecognized reported value remains visible until changed.
 */
export function MusicRateControl(props: MusicRateControlProps): JSX.Element {
  const prefix = () => props.idPrefix ?? TEST_IDS.effectRatePrefix;
  const rates = () => props.rates ?? MUSIC_RATES;
  return (
    <div class="music-rate-control" data-testid={testIdOf('musicRate', { prefix: prefix() })}>
      <span class="music-rate-label" id={`${prefix()}-music-rate-label`}>Speed with the music</span>
      <div class="music-rate-options" role="radiogroup" aria-labelledby={`${prefix()}-music-rate-label`}>
        <Show when={typeof props.value === 'string' && props.value.length > 0
          && !rates().includes(props.value as MusicRate)}>
          <span class="music-rate-option selected" role="radio" aria-checked="true" aria-disabled="true">{props.value}</span>
        </Show>
        <For each={rates()}>
          {(rate) => (
            <button
              type="button"
              role="radio"
              class="music-rate-option"
              classList={{ selected: props.value === rate }}
              aria-checked={props.value === rate}
              aria-label={musicRateLabel(rate)}
              disabled={props.disabled}
              data-testid={testIdOf('musicRateOption', { prefix: prefix(), rate })}
              onClick={() => props.onChange(rate)}
            >{musicRateShortLabel(rate)}</button>
          )}
        </For>
      </div>
    </div>
  );
}
